import type { Ref } from "vue";
import type { LocationQuery } from "vue-router";

import { useQueryCache } from "@pinia/colada";
import { useRoute } from "#app/composables/router";
import { useRequestURL } from "#app/composables/url";
import { onMounted, ref } from "vue";

import { throttle } from "@solvari/utils";

type StatamicPreview = {
  previewSite: Ref<string | null>;
  previewToken: Ref<string | null>;
};

type StatamicPreviewMessage = {
  name: "statamic.preview.updated";
  token: string;
};

function isStatamicPreviewMessage(
  data: unknown,
): data is StatamicPreviewMessage {
  if (typeof data !== "object" || data === null) return false;
  const record = data as Record<string, unknown>;
  return (
    record.name === "statamic.preview.updated" &&
    typeof record.token === "string"
  );
}

const invalidateCms = throttle(() => {
  void useQueryCache().invalidateQueries({ key: ["cms"] });
}, 1000);

// Routing runs outside component context — read the URL, not the route.
function statamicPreviewParams(): {
  previewSite: string | null;
  previewToken: string | null;
} {
  const { searchParams } = useRequestURL();

  return {
    previewSite: searchParams.get("preview_site"),
    previewToken: searchParams.get("token"),
  };
}

// Route middleware runs before `useRoute()` is the target → it passes the target's query
function statamicPreviewFromQuery(query: LocationQuery): StatamicPreview {
  return {
    previewToken: ref((query.token as string | null) ?? null),
    previewSite: ref((query.preview_site as string | null) ?? null),
  };
}

function useStatamicPreview(): StatamicPreview {
  const { previewToken, previewSite } = statamicPreviewFromQuery(
    useRoute().query,
  );

  onMounted(() => {
    window.addEventListener("message", (event: MessageEvent) => {
      if (isStatamicPreviewMessage(event.data)) {
        previewToken.value = event.data.token;
        void invalidateCms();
      }
    });
  });

  return { previewToken, previewSite };
}

export type { StatamicPreview };
export { statamicPreviewFromQuery, statamicPreviewParams, useStatamicPreview };
